/**
 * FormCanvasArea — the form preview/canvas panel for AdvancedFormBuilder.
 * Contains global elements area + section-based drop zones.
 * Extracted from AdvancedFormBuilder.js (Phase 3 split).
 */
import React from 'react';
import ElementPreview from './renderers/ElementPreview';
import { useFormBuilder } from './FormBuilderContext';

export function FormCanvasArea() {
  const {
    sections, globalElements, globalStyles,
    selectedSection, setSelectedSection,
    selectedElement, setSelectedElement,
    draggedElement, dragOverElement, isDragging,
    previewRef, dropZoneRef, fileInputRef,
    addSection, removeSection, updateSection,
    handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd,
    handleFileDrop, handleImageUpload, handleVideoUpload,
    moveElement, moveGlobalElement, removeElement,
    renderResizeHandles,
  } = useFormBuilder();

  return (
    <div className="form-preview" ref={previewRef}>
      {/* Section tab bar */}
      <div className="preview-header">
        <h3>Form Preview</h3>
        <div className="section-tabs">
          <button
            className={`section-tab ${!selectedSection ? 'active' : ''}`}
            onClick={() => setSelectedSection(null)}
          >
            Global Elements
            {globalElements.length > 0 && <span className="element-count">({globalElements.length})</span>}
          </button>
          {sections.map((section) => (
            <button
              key={section.id}
              className={`section-tab ${selectedSection === section.id ? 'active' : ''}`}
              onClick={() => setSelectedSection(section.id)}
            >
              {section.title}
              {section.elements.length > 0 && <span className="element-count">({section.elements.length})</span>}
              {sections.length > 1 && (
                <span className="remove-section" onClick={(e) => { e.stopPropagation(); removeSection(section.id); }}>
                  ×
                </span>
              )}
            </button>
          ))}
          <button className="add-section-tab" onClick={addSection}>+</button>
        </div>
      </div>

      {/* Drop area */}
      <div
        className="preview-area"
        ref={dropZoneRef}
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('drag-over-area'); }}
        onDragLeave={(e) => { e.currentTarget.classList.remove('drag-over-area'); }}
        onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('drag-over-area'); handleFileDrop(e); }}
      >
        <div className="preview-form" style={globalStyles}>
          {/* Global elements zone */}
          {(!selectedSection || selectedSection === 'global') && (
            <div className="global-elements-section">
              <div className="section-header">
                <h4>Global Elements</h4>
                <div className="section-actions">
                  <span className="section-info">Elements placed outside sections</span>
                </div>
              </div>
              <div className="global-elements-content" onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, null, null, true)}>
                {globalElements.map((element, index) => (
                  <div
                    key={element.id}
                    data-id={element.id}
                    className={`form-element global-element ${selectedElement === element.id ? 'selected' : ''} ${draggedElement === element.id ? 'dragging' : ''} ${dragOverElement === element.id ? 'drag-over' : ''}`}
                    onClick={() => setSelectedElement(element.id)}
                    draggable
                    onDragStart={(e) => handleDragStart(e, element.type, 'preview', true)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                  >
                    {renderResizeHandles(element.id, true)}
                    <div className="element-header">
                      {!['header','divider','image','video','map','table','container'].includes(element.type) && (
                        <label style={element.labelStyle} className="element-label">
                          {element.label}
                          {element.required && <span className="required">*</span>}
                          <span className="global-badge">🌍 Global</span>
                        </label>
                      )}
                      <div className="element-actions">
                        <button onClick={(e) => { e.stopPropagation(); if (index > 0) moveGlobalElement(index, index - 1); }} disabled={index === 0}>↑</button>
                        <button onClick={(e) => { e.stopPropagation(); if (index < globalElements.length - 1) moveGlobalElement(index, index + 1); }} disabled={index === globalElements.length - 1}>↓</button>
                        <button onClick={(e) => { e.stopPropagation(); removeElement(element.id, true); }}>🗑️</button>
                      </div>
                    </div>

                    {(element.type === 'image' || element.type === 'video') ? (
                      <div className={`${element.type}-upload-area`} onClick={(e) => { e.stopPropagation(); if (selectedElement === element.id) fileInputRef.current?.click(); }}>
                        <ElementPreview element={element} />
                        <input ref={fileInputRef} type="file" accept={element.type === 'image' ? 'image/*' : 'video/*'} style={{ display: 'none' }}
                          onChange={(e) => { const f = e.target.files[0]; if (f) element.type === 'image' ? handleImageUpload(element.id, f, true) : handleVideoUpload(element.id, f, true); }} />
                      </div>
                    ) : (
                      <ElementPreview element={element} />
                    )}
                    {element.description && <div className="element-description">{element.description}</div>}
                  </div>
                ))}
                {globalElements.length === 0 && (
                  <div className="empty-drop-zone global-drop-zone" onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, null, null, true)}>
                    <p>🌍 Drag and drop global elements here</p>
                    <p className="drop-hint">You can also drop image or video files directly here</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sections */}
          {sections.map(section => (
            <div key={section.id} className={`form-section ${selectedSection === section.id ? 'active' : ''}`} style={section.styling}>
              <div className="section-header">
                <h4 style={section.titleStyling}>{section.title}</h4>
                <div className="section-actions">
                  <select value={section.columns} onChange={(e) => updateSection(section.id, { columns: parseInt(e.target.value) })}>
                    <option value={1}>1 Column</option>
                    <option value={2}>2 Columns</option>
                    <option value={3}>3 Columns</option>
                  </select>
                </div>
              </div>
              <div className={`section-content columns-${section.columns} ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, section.id)}>
                {section.elements.map((element, index) => (
                  <div
                    key={element.id}
                    data-id={element.id}
                    className={`form-element ${selectedElement === element.id ? 'selected' : ''} ${draggedElement === element.id ? 'dragging' : ''} ${dragOverElement === element.id ? 'drag-over' : ''}`}
                    onClick={() => setSelectedElement(element.id)}
                    draggable
                    onDragStart={(e) => handleDragStart(e, element.type, 'preview', false)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                  >
                    {renderResizeHandles(element.id, false)}
                    <div className="element-header">
                      {!['header','divider','image','video','map','table','container'].includes(element.type) && (
                        <label style={element.labelStyle} className="element-label">
                          {element.label}
                          {element.required && <span className="required">*</span>}
                        </label>
                      )}
                      <div className="element-actions">
                        <button onClick={(e) => { e.stopPropagation(); if (index > 0) moveElement(section.id, index, index - 1); }} disabled={index === 0}>↑</button>
                        <button onClick={(e) => { e.stopPropagation(); if (index < section.elements.length - 1) moveElement(section.id, index, index + 1); }} disabled={index === section.elements.length - 1}>↓</button>
                        <button onClick={(e) => { e.stopPropagation(); removeElement(element.id, false); }}>🗑️</button>
                      </div>
                    </div>
                    {(element.type === 'image' || element.type === 'video') ? (
                      <div className={`${element.type}-upload-area`} onClick={(e) => { e.stopPropagation(); if (selectedElement === element.id) fileInputRef.current?.click(); }}>
                        <ElementPreview element={element} />
                        <input ref={fileInputRef} type="file" accept={element.type === 'image' ? 'image/*' : 'video/*'} style={{ display: 'none' }}
                          onChange={(e) => { const f = e.target.files[0]; if (f) element.type === 'image' ? handleImageUpload(element.id, f, false) : handleVideoUpload(element.id, f, false); }} />
                      </div>
                    ) : (
                      <ElementPreview element={element} />
                    )}
                    {element.description && <div className="element-description">{element.description}</div>}
                  </div>
                ))}
                {section.elements.length === 0 && (
                  <div className="empty-drop-zone" onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, section.id)}>
                    <p>Drag and drop form elements here or use the sidebar to add elements</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
